/**
 * MKS-345 — TanStack Query bindings for the configurable sales pipeline.
 *
 * Mirrors `useProjectStates.ts` (the board's per-project column hooks) so the
 * shared `StateListEditor` gets the same callback shapes on both surfaces.
 * Every mutation invalidates `['leads']` as well as `['leadStages']`: a column
 * rename or recolour changes what the board paints, and a delete can only
 * happen on an empty column but still removes one.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  fetchLeadStages,
  createLeadStageApi,
  updateLeadStageApi,
  deleteLeadStageApi,
  reorderLeadStagesApi,
  fetchLeadStageHistory,
  type CreateLeadStageInput,
  type UpdateLeadStageInput,
} from '../services/api/leadStages';

export const LEAD_STAGES_KEY = ['leadStages'];

export function useLeadStages() {
  return useQuery({
    queryKey: LEAD_STAGES_KEY,
    queryFn: fetchLeadStages,
    // The pipeline changes a handful of times a year; refetching it on every
    // board mount buys nothing.
    staleTime: 5 * 60_000,
  });
}

function useStageMutationInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: LEAD_STAGES_KEY });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };
}

export function useCreateLeadStage() {
  const invalidate = useStageMutationInvalidation();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateLeadStageInput) => createLeadStageApi(input),
    onSuccess: invalidate,
    onError: (err) => onError(err, "Couldn't add stage"),
  });
}

export function useUpdateLeadStage() {
  const invalidate = useStageMutationInvalidation();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & UpdateLeadStageInput) =>
      updateLeadStageApi(id, patch),
    onSuccess: invalidate,
    onError: (err) => onError(err, "Couldn't update stage"),
  });
}

/**
 * The 409 the server returns while leads still sit in the column carries a
 * message naming how many, and `useErrorToast` surfaces it verbatim — which is
 * the "deleting a stage that has leads is refused" feedback the ticket asks
 * for, without a second modal.
 */
export function useDeleteLeadStage() {
  const invalidate = useStageMutationInvalidation();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteLeadStageApi(id),
    onSuccess: invalidate,
    onError: (err) => onError(err, "Couldn't delete stage"),
  });
}

export function useReorderLeadStages() {
  const invalidate = useStageMutationInvalidation();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (stageIds: number[]) => reorderLeadStagesApi(stageIds),
    onSuccess: invalidate,
    onError: (err) => onError(err, "Couldn't reorder stages"),
  });
}

/** The lead's stage trail, loaded only while the lead modal has it open. */
export function useLeadStageHistory(leadId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['leadStageHistory', leadId],
    queryFn: () => fetchLeadStageHistory(leadId!),
    enabled: leadId !== null && enabled,
  });
}
